import { message } from 'antd';

/**
 * Error handling utilities for Enhanced SMART Goals
 */

/**
 * Handle API errors with user-friendly messages
 * @param {Error} error - Error object from API call
 * @param {string} operation - Description of the operation that failed
 * @returns {string} - User-friendly error message
 */
export const handleApiError = (error, operation = 'operation') => {
  console.error(`SMART Goals API Error (${operation}):`, error);
  
  let userMessage = `Failed to ${operation}. Please try again.`;
  
  if (error.response) {
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        if (data.errors && Array.isArray(data.errors)) {
          userMessage = `Validation errors: ${data.errors.join(', ')}`;
        } else if (data.message) {
          userMessage = data.message;
        } else {
          userMessage = `Invalid data provided for ${operation}`;
        }
        break;
        
      case 401:
        userMessage = 'You are not authorized to perform this action. Please log in again.';
        break;
        
      case 403:
        userMessage = 'You do not have permission to perform this action.';
        break;
        
      case 404:
        userMessage = 'The requested resource was not found.';
        break;
        
      case 409:
        userMessage = data.message || 'A conflict occurred. The resource may have been modified by another user.';
        break;
        
      case 422:
        if (data.errors && Array.isArray(data.errors)) {
          userMessage = `Validation failed: ${data.errors.join(', ')}`;
        } else {
          userMessage = data.message || 'The provided data is invalid.';
        }
        break;
        
      case 429:
        userMessage = 'Too many requests. Please wait a moment and try again.';
        break;
        
      case 500:
        userMessage = 'Server error occurred. Please try again later or contact support.';
        break;
        
      case 503:
        userMessage = 'Service temporarily unavailable. Please try again later.';
        break;
        
      default:
        userMessage = data.message || `An unexpected error occurred during ${operation}`;
    }
  } else if (error.request) {
    userMessage = 'Network error. Please check your internet connection and try again.';
  } else {
    userMessage = error.message || `An unexpected error occurred during ${operation}`;
  }
  
  return userMessage;
};

/**
 * Show error message to user
 * @param {Error} error - Error object
 * @param {string} operation - Operation description
 * @param {number} duration - Message duration in seconds
 */
export const showErrorMessage = (error, operation = 'operation', duration = 5) => {
  const userMessage = handleApiError(error, operation);
  message.error(userMessage, duration);
};

/**
 * Handle validation errors from form submission
 * @param {Object} validationResult - Result from validation function
 * @param {string} context - Context for the validation
 */
export const handleValidationErrors = (validationResult, context = 'form') => {
  if (!validationResult.isValid && validationResult.errors.length > 0) {
    const errorMessage = `${context} validation failed: ${validationResult.errors.join(', ')}`;
    message.error(errorMessage, 6);
    return false;
  }
  return true;
};

/**
 * Retry mechanism for API calls
 * @param {Function} apiCall - Function that makes the API call
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} delay - Delay between retries in milliseconds
 * @returns {Promise} - Promise that resolves with API response
 */
export const retryApiCall = async (apiCall, maxRetries = 3, delay = 1000) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error;
      
      // Don't retry on client errors (4xx) except for 429 (rate limit)
      if (error.response && error.response.status >= 400 && error.response.status < 500 && error.response.status !== 429) {
        throw error;
      }
      
      if (attempt < maxRetries) {
        console.warn(`API call attempt ${attempt} failed, retrying in ${delay}ms...`, error.message);
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2; // Exponential backoff
      }
    }
  }
  
  throw lastError;
};

/**
 * Handle file upload errors
 * @param {Error} error - Upload error
 * @param {string} fileName - Name of the file being uploaded
 */
export const handleFileUploadError = (error, fileName = 'file') => {
  console.error('File upload error:', error);
  
  let userMessage = `Failed to upload ${fileName}. Please try again.`;
  
  if (error.response) {
    const { status, data } = error.response;
    
    switch (status) {
      case 413:
        userMessage = `File ${fileName} is too large. Please choose a smaller file (max 10MB).`;
        break;
      case 415:
        userMessage = `File type not supported for ${fileName}. Please upload PDF, Word documents, or images.`;
        break;
      case 422:
        userMessage = data.message || `Invalid file format for ${fileName}.`;
        break;
      default:
        userMessage = handleApiError(error, `upload ${fileName}`);
    }
  }
  
  message.error(userMessage, 5);
  return userMessage;
};

/**
 * Handle action management errors
 * @param {Error} error - Action error
 * @param {string} actionType - Type of action (add, complete, remove)
 * @param {string} actionDescription - Description of the action
 */
export const handleActionError = (error, actionType, actionDescription = '') => {
  console.error(`Action ${actionType} error:`, error);
  
  const actionName = actionDescription ? `"${actionDescription}"` : 'action';
  let userMessage = `Failed to ${actionType} ${actionName}. Please try again.`;
  
  if (error.response) {
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        if (actionType === 'complete' && data.message?.includes('already completed')) {
          userMessage = `${actionName} is already completed.`;
        } else if (actionType === 'remove' && data.message?.includes('not found')) {
          userMessage = `${actionName} not found or already removed.`;
        } else {
          userMessage = data.message || `Invalid request to ${actionType} ${actionName}.`;
        }
        break;
      case 404:
        userMessage = 'SMART goal not found. It may have been deleted by another user.';
        break;
      default:
        userMessage = handleApiError(error, `${actionType} ${actionName}`);
    }
  }
  
  message.error(userMessage, 4);
  return userMessage;
};

/**
 * Handle review scheduling errors
 * @param {Error} error - Review error
 * @param {string} operation - Review operation (schedule, complete, update)
 */
export const handleReviewError = (error, operation = 'schedule review') => {
  console.error('Review scheduling error:', error);
  
  let userMessage = `Failed to ${operation}. Please try again.`;
  
  if (error.response) {
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        if (data.message?.includes('past')) {
          userMessage = 'Review date cannot be in the past. Please select a future date.';
        } else {
          userMessage = data.message || `Invalid data for ${operation}.`;
        }
        break;
      case 404:
        userMessage = 'SMART goal not found. It may have been deleted.';
        break;
      default:
        userMessage = handleApiError(error, operation);
    }
  }
  
  message.error(userMessage, 4);
  return userMessage;
};

/**
 * Handle education material errors
 * @param {Error} error - Material error
 * @param {string} operation - Material operation (add, remove, view)
 * @param {string} materialTitle - Title of the material
 */
export const handleEducationMaterialError = (error, operation, materialTitle = '') => {
  console.error('Education material error:', error);
  
  const materialName = materialTitle ? `"${materialTitle}"` : 'education material';
  let userMessage = `Failed to ${operation} ${materialName}. Please try again.`;
  
  if (error.response) {
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        if (operation === 'add' && data.message?.includes('URL')) {
          userMessage = 'Invalid URL provided. Please check the link and try again.';
        } else {
          userMessage = data.message || `Invalid request to ${operation} ${materialName}.`;
        }
        break;
      case 404:
        if (operation === 'view' || operation === 'download') {
          userMessage = `${materialName} not found or no longer available.`;
        } else {
          userMessage = 'SMART goal not found.';
        }
        break;
      default:
        userMessage = handleApiError(error, `${operation} ${materialName}`);
    }
  }
  
  message.error(userMessage, 4);
  return userMessage;
};

/**
 * Handle completion rate calculation errors
 * @param {Error} error - Calculation error
 * @param {Array} actions - Actions array that caused the error
 */
export const handleCompletionRateError = (error, actions = []) => {
  console.error('Completion rate calculation error:', error);
  
  let userMessage = 'Failed to calculate completion rate. Using default value.';
  
  if (actions.length === 0) {
    userMessage = 'No actions available for completion rate calculation.';
  } else {
    userMessage = 'Error calculating progress. Please refresh the page.';
  }
  
  message.warning(userMessage, 3);
  return 0; // Return default completion rate
};

/**
 * Create error boundary for SMART Goals components
 * @param {Error} error - Component error
 * @param {string} componentName - Name of the component
 */
export const handleComponentError = (error, componentName = 'component') => {
  console.error(`${componentName} error:`, error);
  
  const userMessage = `An error occurred in ${componentName}. Please refresh the page or contact support if the problem persists.`;
  message.error(userMessage, 6);
  
  return {
    hasError: true,
    error: error.message || 'Unknown error',
    componentName
  };
};

/**
 * Validate network connectivity
 * @returns {boolean} - Whether network is available
 */
export const checkNetworkConnectivity = () => {
  if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
    return navigator.onLine;
  }
  return true; // Assume online if can't detect
};

/**
 * Handle offline scenarios
 * @param {string} operation - Operation being attempted
 */
export const handleOfflineError = (operation = 'operation') => {
  if (!checkNetworkConnectivity()) {
    const userMessage = `Cannot ${operation} while offline. Please check your internet connection.`;
    message.error(userMessage, 5);
    return true;
  }
  return false;
};
